import { markPosthogInitialized } from '@civilgrid/ui/analytics';
import type { RouterHistory } from '@tanstack/react-router';
import posthog, { type PostHog, type PostHogConfig } from 'posthog-js';
import { proxy } from 'valtio';

let isInitialized = false;

// The one `$pageview` per page load is sent by `capturePosthogInitialPageview`, not the SDK, so it
// can wait for the org group. Requested/captured are separate: the request can arrive before opt-in.
let initialPageviewRequested = false;
let initialPageviewCaptured = false;

/**
 * Which organization the in-memory flags payload was evaluated for. Flags targeting the
 * `organization` group (e.g. `conflict-notifications-enabled`) are only meaningful for the org whose group
 * was active when the flags response was fetched — after an org switch the cached values are
 * the *previous* org's until the group-change reload lands. `null` = no response has landed
 * for the org most recently passed to `setPosthogOrganization` (or no org set yet).
 */
export const posthogFlagsStore = proxy<{ flagsResolvedForOrgId: string | null }>({
  flagsResolvedForOrgId: null,
});

// The org whose flags response we are waiting on. Module-level (not in the proxy): only the
// resolved marker is a render input.
let pendingFlagsOrgId: string | null = null;

/**
 * Defensive client-side feature-flag read. Returns `false` (legacy behavior) if
 * PostHog is unavailable or throws, so a flag hiccup never breaks a render path.
 */
export function isFeatureFlagEnabled(flag: string): boolean {
  try {
    return posthog.isFeatureEnabled(flag) === true;
  } catch {
    return false;
  }
}

export function initPosthog(token: string, config?: Partial<PostHogConfig>): PostHog {
  if (isInitialized) {
    console.warn('Posthog already initialized');
    return posthog;
  }

  if (!token) {
    console.warn('PostHog API key not found');
    return posthog;
  }

  const defaultConfig: Partial<PostHogConfig> = {
    api_host: 'https://us.i.posthog.com',
    autocapture: true,
    capture_pageleave: true,
    enable_heatmaps: true,
    persistence: 'localStorage',
    opt_out_capturing_by_default: true,
    disable_product_tours: false,
  };

  // Automation (Playwright/Selenium set `navigator.webdriver`) still initializes so feature
  // flags evaluate for real — e2e must exercise the same flag-gated paths users get — but with
  // every observation surface forced off. Combined with the opt-in refusal in
  // `enablePosthogCapturing`, a webdriver session sends only `/flags` requests: no events,
  // recordings, heatmaps, surveys, or tours.
  const automationOverrides: Partial<PostHogConfig> = navigator.webdriver
    ? {
        autocapture: false,
        capture_pageleave: false,
        enable_heatmaps: false,
        disable_session_recording: true,
        disable_surveys: true,
        disable_product_tours: true,
      }
    : {};

  const mergedConfig = {
    ...defaultConfig,
    ...config,
    // The SDK's initial `$pageview` fires at opt-in, before any org group exists; this module sends
    // it instead (see `capturePosthogInitialPageview`).
    capture_pageview: false,
    ...automationOverrides,
  };

  posthog.init(token, mergedConfig);
  posthog.register({ platform: 'web' });
  isInitialized = true;
  markPosthogInitialized();

  // ONE persistent handler resolves the pending org on every flags response (initial load and
  // each group-change reload). Must be registered once here, not per org switch: `onFeatureFlags`
  // invokes a newly registered callback immediately when flags are already loaded, which would
  // mark the new org resolved against the previous org's values. (A response that was already
  // in flight when the group changed can still land here early — the SDK debounces reloads and
  // the group change always queues a fresh fetch, so values self-correct on the next response
  // and reactive consumers converge.)
  posthog.onFeatureFlags(() => {
    posthogFlagsStore.flagsResolvedForOrgId = pendingFlagsOrgId;
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !posthog.has_opted_out_capturing()) {
      posthog.startSessionRecording();
    }
  });

  return posthog;
}

export function enablePosthogCapturing(): void {
  if (!isInitialized) return;
  // Automation is initialized only so feature flags evaluate (see `initPosthog`); events must
  // stay opted out for the session's whole life.
  if (navigator.webdriver) return;

  // No `$opt_in` event: it would leave before the org group is registered.
  posthog.opt_in_capturing({ captureEventName: false });
  flushInitialPageview();
}

/**
 * Sends the single `$pageview` for this page load. Call it once the org group is registered so the
 * event carries `$groups`; if capture is not opted in yet, the pageview goes out at opt-in.
 * Later calls (org switches) are no-ops.
 */
export function capturePosthogInitialPageview(): void {
  initialPageviewRequested = true;
  flushInitialPageview();
}

function flushInitialPageview(): void {
  if (!initialPageviewRequested || initialPageviewCaptured) return;
  if (!isInitialized || navigator.webdriver || !posthog.has_opted_in_capturing()) return;
  // Background and prerendered loads count only once the tab is shown, matching the SDK.
  if (document.visibilityState !== 'visible') {
    document.addEventListener('visibilitychange', flushInitialPageview, { once: true });
    return;
  }

  initialPageviewCaptured = true;
  posthog.capture('$pageview', { title: document.title }, { send_instantly: true });
}

type HistorySubscriberAction = Parameters<Parameters<RouterHistory['subscribe']>[0]>[0]['action'];

// Same values posthog-js sends in its own `capture_pageview: 'history_change'` mode.
function navigationTypeOf(action: HistorySubscriberAction): 'pushState' | 'replaceState' | 'popstate' {
  switch (action.type) {
    case 'PUSH':
      return 'pushState';
    case 'REPLACE':
      return 'replaceState';
    default:
      return 'popstate';
  }
}

/**
 * Sends a `$pageview` for every in-app navigation that changes the pathname, tagged with
 * `navigation_type` (`pushState` | `replaceState` | `popstate`). Search-only and hash-only updates
 * send nothing. Route changes before the initial pageview (bootstrap redirects) send nothing either:
 * that one event records the landing route. Returns the unsubscribe function.
 */
export function trackPosthogRoutePageviews(history: RouterHistory): () => void {
  let lastPathname = history.location.pathname;
  return history.subscribe(({ location, action }) => {
    const pathnameChanged = location.pathname !== lastPathname;
    lastPathname = location.pathname;
    if (!pathnameChanged || !initialPageviewCaptured) return;

    // TanStack notifies before it commits the browser URL (one microtask later), and posthog-js
    // reads `window.location` at capture time.
    queueMicrotask(() => {
      // Logout opts capture back out within the same page load.
      if (!posthog.has_opted_in_capturing()) return;
      posthog.capture('$pageview', { navigation_type: navigationTypeOf(action) });
    });
  });
}

export function setPosthogUser(user: { id: string; email: string }): void {
  if (!isInitialized) return;

  posthog.identify(user.id, {
    email: user.email,
  });
}

export function setPosthogPersonProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) return;

  posthog.setPersonProperties(properties);
}

export function setPosthogOrganization(organization: { id: string; name: string }): void {
  if (!isInitialized) {
    // No flags will ever load (missing API key / webdriver): resolve immediately so consumers
    // fall through to flag-OFF behavior instead of holding on a loader forever.
    pendingFlagsOrgId = organization.id;
    posthogFlagsStore.flagsResolvedForOrgId = organization.id;
    return;
  }

  if (pendingFlagsOrgId !== organization.id) {
    // The in-memory flags were evaluated for the previous org's group — stale until the reload
    // triggered below lands. (A same-org re-set keeps the marker: the current values stay valid
    // while the property-refresh reload runs.)
    pendingFlagsOrgId = organization.id;
    posthogFlagsStore.flagsResolvedForOrgId = null;
  }

  // Passing group properties always triggers a flags reload (`setGroupPropertiesForFlags`
  // defaults `reloadFeatureFlags` to true), so the pending org always gets a response — even
  // when the group key is unchanged.
  posthog.group('organization', organization.id, {
    name: organization.name,
  });
}

export function clearPosthogContext(): void {
  pendingFlagsOrgId = null;
  posthogFlagsStore.flagsResolvedForOrgId = null;
  if (!isInitialized) return;

  posthog.reset();
  posthog.opt_out_capturing();
}

export function resetPosthog(): void {
  pendingFlagsOrgId = null;
  posthogFlagsStore.flagsResolvedForOrgId = null;
  if (!isInitialized) return;

  posthog.reset();
}

export function isPosthogInitialized(): boolean {
  return isInitialized;
}
